import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showTokkoChat } from '@/store/slices/components/chat/MessengerSlice';
import { handleSender } from '@/store/slices/components/sender/SenderSlice';
import useWhatsappOrSender from '../shared/Hooks/WhatsappOrSender/useWhatsappOrSender';

const WSP = 'whatsapp';
const EMAIL = 'email';
const TOKKO_CHAT = 'tokko_chat';

/**
 * Custom hook that manages ParticipantCard button actions and states.
 * Centralizes chat logic, sender, and channel management with internal states.
 *
 * @param {Object} participant - Participant data
 * @param {string} type - Participant type ('contact' or 'agent')
 * @returns {Object} - Handlers and states needed for ParticipantCard
 */
const useParticipantActions = (participant, type = 'contact') => {
  const dispatch = useDispatch();
  const {
    show: senderShow,
    isSendingEmail,
    isPreparingWhatsapp,
  } = useSelector((state) => state.SenderSlice);
  const {
    company_id,
    id: ownerID,
    user_language,
  } = useSelector((state) => state.UserSlice.data);
  const { tokkoMessengerURL } = useSelector(
    (state) => state.UserPreferencesSlice
  );

  const { handleWhatsappAction, loading: whatsappLoading } =
    useWhatsappOrSender({
      contactId: participant.id,
    });

  // Agents state for agent type participants
  const [agentsState, setAgentsState] = useState({
    loading: false,
    error: null,
    validAgentsChat: false,
    agentCID: null,
  });

  /**
   * Fetches and manages one-to-one agent chat channel
   */
  const fetchAgentsChatChannel = async (agentId) => {
    setAgentsState((prev) => ({ ...prev, loading: true, error: null }));
    const accessToken = localStorage.getItem('jwt');
    const payload = {
      members: [
        {
          id: ownerID,
          company: { id: company_id, languague: user_language, name: '' },
        },
        {
          id: agentId,
          company: { id: company_id, languague: user_language, name: '' },
        },
      ],
    };

    try {
      const response = await fetch(
        `${tokkoMessengerURL}/users/me/channel/one_to_one/get_or_create`,
        {
          method: 'POST',
          credentials: 'include',
          body: JSON.stringify(payload),
          headers: {
            Authorization: accessToken,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await response.json();
      const cid = result.cid || null;

      setAgentsState({
        loading: false,
        error: null,
        validAgentsChat: Boolean(cid),
        agentCID: cid,
      });
    } catch (error) {
      setAgentsState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Error fetching agent chat channel',
        validAgentsChat: false,
        agentCID: null,
      }));
    }
  };

  /**
   * Action handlers organized by action type
   */
  const actionHandlers = {
    [EMAIL]: (participantId) => {
      const senderPayload = {
        show: EMAIL,
        preSelectedContacts: [participantId],
      };
      dispatch(handleSender(senderPayload));
    },

    [WSP]: (participantId) => {
      handleWhatsappAction(participantId);
    },

    [TOKKO_CHAT]: () => {
      if (
        type === 'agent' &&
        agentsState.validAgentsChat &&
        agentsState.agentCID
      ) {
        const chatPayload = {
          show: true,
          CID: agentsState.agentCID,
        };
        dispatch(showTokkoChat(chatPayload));
      }
    },
  };

  /**
   * Main handler for button clicks
   */
  const handleButtonClick = (buttonType, participantId) => {
    const handler = actionHandlers[buttonType];
    if (handler) {
      handler(participantId);
    } else {
      console.warn(`Unsupported button type: ${buttonType}`);
    }
  };

  /**
   * Disabled state configuration by button type
   */
  const disabledStates = {
    [EMAIL]: () => isSendingEmail,
    [WSP]: () => isPreparingWhatsapp || whatsappLoading,
    [TOKKO_CHAT]: () => agentsState.loading || !agentsState.validAgentsChat,
  };

  /**
   * Determines if a button should be disabled
   */
  const isButtonDisabled = (buttonType) => {
    const checkDisabled = disabledStates[buttonType];
    return checkDisabled ? checkDisabled() : false;
  };

  /**
   * Effect to fetch agent chat channel
   */
  useEffect(() => {
    if (type === 'agent' && participant?.id) {
      if (ownerID === participant?.id) return;
      fetchAgentsChatChannel(participant.id);
    }
  }, [participant?.id, type]);

  /**
   * Consolidated loading states
   */
  const loadingStates = {
    chat: whatsappLoading,
    email: isSendingEmail,
    whatsapp: isPreparingWhatsapp,
    tokkoChat: agentsState.loading,
  };

  /**
   * Validation states
   */
  const validationStates = {
    agentChatChannel: agentsState.agentCID,
    validAgentsChat: agentsState.validAgentsChat,
    error: agentsState.error,
  };

  return {
    handleButtonClick,

    // States
    loadingStates,
    validationStates,
    isButtonDisabled,

    // Individual states for compatibility
    senderShow,
    isSendingEmail,
    isPreparingWhatsapp,
    ownerID,
    agentChatError: agentsState.error,
    agentCID: agentsState.agentCID,
    agentChatLoading: agentsState.loading,
  };
};

export default useParticipantActions;
